import { useState } from 'react';
import type { Plan } from '@calwmp/shared';

interface ComplianceReportProps {
  plan: Plan;
}

/**
 * Compliance report download component (T109)
 * Allows downloading PDF CalGreen 5.408 compliance report
 */
export function ComplianceReport({ plan }: ComplianceReportProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadReport = async () => {
    setIsDownloading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/compliance/plans/${plan.id}/report`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download report');
      }

      // Create download link
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `calgreen-report-${plan.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download report');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">CalGreen 5.408 Compliance Report</h3>
      </div>
      <div className="card-body">
        <p className="text-muted mb-4">
          Download your official CalGreen Section 5.408 Construction Waste Management Plan
          compliance report. This PDF includes all project information, waste streams, and
          compliance calculations formatted per California Building Standards Code.
        </p>

        {error && (
          <div className="alert alert-error mb-4">
            <strong>Error:</strong> {error}
          </div>
        )}

        <button
          onClick={downloadReport}
          disabled={isDownloading}
          className="btn btn-primary"
        >
          {isDownloading ? (
            <>
              <span className="spinner-sm mr-2" />
              Generating PDF...
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download PDF Report
            </>
          )}
        </button>
      </div>
    </div>
  );
}